<template>
  <div class="prize-modal-overlay" @click="$emit('close')">
    <div class="prize-modal" @click.stop>
      <div class="modal-header">
        <h3>
          <i :class="isSalesMode ? 'fa-solid fa-cash-register' : 'fa-solid fa-trophy'"></i>
          {{ modalTitle }} — {{ entityName }}
        </h3>
        <button class="btn-close" @click="$emit('close')">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
      <div class="modal-subheader">
        <span><i class="fa-solid fa-calendar"></i> {{ formatDate(dateBegin) }} até {{ formatDate(dateEnd) }}</span>
        <span v-if="!loading && tickets.length" class="subheader-total">
          <i class="fa-solid fa-calculator"></i>
          Total: {{ setCurrencyString(ticketsTotal) }} · {{ tickets.length }} bilhete(s)
        </span>
      </div>
      <div class="modal-body">
        <div v-if="loading" class="loading-state">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <span>{{ isSalesMode ? 'Carregando vendas...' : 'Carregando bilhetes premiados...' }}</span>
        </div>
        <div v-else-if="error" class="error-state">
          <i class="fa-solid fa-exclamation-triangle"></i>
          <p>{{ error }}</p>
        </div>
        <div v-else-if="!tickets.length" class="empty-state">
          <i :class="isSalesMode ? 'fa-solid fa-cash-register' : 'fa-solid fa-trophy'"></i>
          <p>{{ isSalesMode ? 'Nenhuma venda no período.' : 'Nenhum bilhete premiado no período.' }}</p>
        </div>
        <div v-else class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th>NSU</th>
                <th v-if="showSellerColumn">Vendedor</th>
                <th>Data/Hora Aposta</th>
                <th>Valor</th>
                <th>Status</th>
                <th v-if="!isSalesMode">Premiação</th>
                <th>Comissão</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ticket in tickets" :key="ticket.betId" class="data-row">
                <td>
                  <span class="nsu-code">{{ ticket.nsu }}</span>
                  <span
                    v-if="(ticket.packageDrawCount || 1) > 1"
                    class="package-badge"
                    title="Bingo Semanal — pacote de extrações"
                  >
                    {{ ticket.packageDrawCount }}x
                  </span>
                </td>
                <td v-if="showSellerColumn">{{ ticket.sellerName || '—' }}</td>
                <td>
                  <span class="date">{{ formatDate(ticket.dateTimePersisted) }}</span>
                  <span class="time">{{ formatTime(ticket.dateTimePersisted) }}</span>
                </td>
                <td><span class="currency-value">{{ setCurrencyString(ticket.amount) }}</span></td>
                <td>
                  <span class="status-badge" :class="getStatusClass(ticket)">
                    <i :class="getStatusIcon(ticket)"></i>
                    {{ getStatusText(ticket) }}
                  </span>
                </td>
                <td v-if="!isSalesMode">
                  <span class="currency-value currency-positive">
                    {{ setCurrencyString(ticket.totalAwarded) }}
                  </span>
                </td>
                <td><span class="currency-value">{{ setCurrencyString(ticket.commission) }}</span></td>
                <td>
                  <button
                    type="button"
                    class="btn-action btn-view"
                    @click="viewTicketDetails(ticket.betId)"
                    title="Ver detalhes"
                  >
                    <i class="fa-solid fa-eye"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes do Bilhete -->
    <div v-if="showDetailsModal" class="inner-modal-overlay" @click="closeDetailsModal">
      <div class="inner-modal-content" @click.stop>
        <div class="modal-header">
          <h3>Detalhes do Bilhete</h3>
          <button class="btn-close" @click="closeDetailsModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="ticketDetails" class="ticket-details">
            <div class="detail-group">
              <label>NSU:</label>
              <span>{{ ticketDetails.nsu }}</span>
            </div>
            <div class="detail-group">
              <label>Vendedor:</label>
              <span>{{ ticketDetails.sellerName }}</span>
            </div>
            <div class="detail-group">
              <label>Status:</label>
              <span>{{ getStatusWithCancelDate(ticketDetails) }}</span>
            </div>
            <div class="detail-group">
              <label><i class="fa-solid fa-clock"></i> Data/Hora da Aposta:</label>
              <span>{{ formatDateTime(ticketDetails.betDateTimePersisted) }}</span>
            </div>
            <div class="detail-group">
              <label><i class="fa-solid fa-calendar-check"></i> Data do Sorteio:</label>
              <div v-for="lottery in (ticketDetails.lotteries || [])" :key="lottery.lotteryDetailId" class="lottery-draw-item">
                <span class="lottery-name">{{ lottery.lotteryDetailName }}</span>
                <span class="draw-date">{{ formatTicketDrawDate(ticketDetails) }}</span>
              </div>
            </div>
            <div class="detail-group">
              <label>Valor Total:</label>
              <span>{{ setCurrencyString(ticketDetails.amount) }}</span>
            </div>
            <div class="detail-group">
              <label>Comissão:</label>
              <span>{{ setCurrencyString(ticketDetails.commission) }}</span>
            </div>
            <div v-if="getTotalPrizeValue(ticketDetails) > 0" class="detail-group">
              <label><i class="fa-solid fa-trophy"></i> Valor do Prêmio:</label>
              <span>{{ setCurrencyString(getTotalPrizeValue(ticketDetails)) }}</span>
            </div>
            <div v-if="isPrizeClaimedInDetails(ticketDetails)" class="detail-group">
              <label><i class="fa-solid fa-check-circle"></i> Status do Prêmio:</label>
              <span class="badge badge-success"><i class="fa-solid fa-check"></i> RESGATADO</span>
            </div>
            <div v-else-if="getTotalPrizeValue(ticketDetails) > 0" class="detail-group">
              <label><i class="fa-solid fa-clock"></i> Status do Prêmio:</label>
              <span class="badge badge-warning"><i class="fa-solid fa-clock"></i> NÃO RESGATADO</span>
            </div>
            <div v-if="ticketDetails.prizeClaimedDate" class="detail-group">
              <label><i class="fa-solid fa-calendar-check"></i> Data do Resgate:</label>
              <span>{{ formatDateTime(ticketDetails.prizeClaimedDate) }}</span>
            </div>
            <div v-if="ticketDetails.prizeClaimedBy" class="detail-group">
              <label><i class="fa-solid fa-user"></i> Resgatado por:</label>
              <span>{{ ticketDetails.prizeClaimedByName || ticketDetails.prizeClaimedBy }}</span>
            </div>
            <!-- Loterias e Números -->
            <div class="lotteries-section">
              <h4><i class="fa-solid fa-ticket"></i> Bilhetes e Números</h4>
              <div
                v-for="lottery in (ticketDetails.lotteries || [])"
                :key="lottery.lotteryDetailId"
                class="lottery-item"
              >
                <div class="lottery-header">
                  <div class="lottery-title">
                    <i class="fa-solid fa-clover"></i>
                    <h5>{{ lottery.lotteryDetailName }}</h5>
                  </div>
                  <div class="lottery-draw-date">
                    <i class="fa-solid fa-calendar-check"></i>
                    <span>{{ formatTicketDrawDate(ticketDetails) }}</span>
                  </div>
                </div>
                <div
                  v-for="group in groupNumbersByBetGroup(lottery.numbers || [])"
                  :key="group.groupOrder"
                  class="bet-group"
                >
                  <div class="bet-group-header">
                    <div class="bet-group-title">
                      <i class="fa-solid fa-layer-group"></i>
                      <span>JOGO PRINCIPAL</span>
                    </div>
                  </div>
                  <div
                    v-for="(modalityNumbers, modalityIndex) in getModalitiesInGroup(group.numbers)"
                    :key="group.groupOrder + '-' + modalityIndex"
                    class="modality-group"
                  >
                    <div class="modality-header">
                      <div class="modality-name-container">
                        <span class="modality-name">{{ modalityNumbers[0].modalityDescription }}</span>
                        <span v-if="(modalityNumbers[0].plusValueType || 0) > 0" class="valendo-badge">+ VALENDO</span>
                      </div>
                      <span class="modality-placing">{{ modalityNumbers[0].placing }}</span>
                    </div>
                    <div class="numbers-container">
                      <div
                        v-for="number in modalityNumbers"
                        :key="number.number"
                        class="number-badge"
                        :class="{ 'number-awarded': number.numberAwards && number.numberAwards.length > 0 }"
                      >
                        <div class="number-value">{{ number.number }}</div>
                        <div class="number-amount">{{ setCurrencyString(number.value) }}</div>
                        <div v-if="number.numberAwards && number.numberAwards.length > 0" class="number-award-indicator">
                          <i class="fa-solid fa-trophy"></i>
                          <span>{{ setCurrencyString(getTotalAwardValue(number.numberAwards)) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";
import { groupBingoSemanalTickets } from "@/utils/bingoSemanalTickets";

export default {
  name: "PrizeTicketsModal",
  props: {
    regionId: { type: String, default: null },
    sellerId: { type: String, default: null },
    regionName: { type: String, default: "" },
    dateBegin: { type: String, required: true },
    dateEnd: { type: String, required: true },
    /** 'prizes' = bilhetes premiados | 'sales' = vendas do período */
    mode: { type: String, default: "prizes" },
  },
  emits: ["close"],
  data() {
    return {
      loading: true,
      error: null,
      tickets: [],
      showDetailsModal: false,
      ticketDetails: null,
    };
  },
  computed: {
    isSalesMode() {
      return String(this.mode || "").toLowerCase() === "sales";
    },
    entityName() {
      return this.regionName || "—";
    },
    modalTitle() {
      return this.isSalesMode ? "Vendas" : "Bilhetes Premiados";
    },
    showSellerColumn() {
      return this.isSalesMode && !this.sellerId;
    },
    ticketsTotal() {
      return this.tickets.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
    },
  },
  mounted() {
    this.loadTickets();
  },
  methods: {
    async loadTickets() {
      this.loading = true;
      this.error = null;
      this.tickets = [];
      try {
        const pageSize = 1000;
        const maxPages = 200;
        const all = [];
        let pageIndex = 1;
        let totalPages = null;

        while (pageIndex <= maxPages) {
          const params = new URLSearchParams({
            startDate: this.dateBegin,
            endDate: this.dateEnd,
            pageIndex: String(pageIndex),
          });

          let response;
          if (this.sellerId) {
            params.set("sellerId", this.sellerId);
            if (this.regionId) {
              params.set("regionId", this.regionId);
            }
            response = await mainService.searchBetsBySeller(params.toString());
          } else {
            if (!this.regionId) {
              this.error = "Região não informada.";
              return;
            }
            params.set("regionId", this.regionId);
            response = await mainService.searchBalanceBets(params.toString());
          }

          const data = response?.data;
          let items = [];
          if (Array.isArray(data?.result)) {
            items = data.result;
            totalPages = data.totalPages ?? data.TotalPages ?? totalPages;
          } else if (Array.isArray(data)) {
            items = data;
          }

          all.push(...items);
          if (!items.length) break;
          if (totalPages != null && pageIndex >= totalPages) break;
          if (items.length < pageSize) break;
          pageIndex += 1;
        }

        let list;
        if (this.isSalesMode) {
          // Mesmo critério do fechamento: apenas bilhetes não cancelados
          list = all.filter((t) => !(t.canceled === true || t.canceled === "true"));
        } else {
          list = all.filter((t) => parseFloat(t.totalAwarded || 0) > 0);
        }
        this.tickets = groupBingoSemanalTickets(list);
        this.tickets.sort((a, b) => new Date(b.dateTimePersisted) - new Date(a.dateTimePersisted));
      } catch (err) {
        this.error = handleErrorMessage(err);
      } finally {
        this.loading = false;
      }
    },
    async viewTicketDetails(betId) {
      try {
        const response = await mainService.searchBetById(betId);
        this.ticketDetails = response.data;
        this.showDetailsModal = true;
      } catch {
        this.error = "Erro ao carregar detalhes do bilhete.";
      }
    },
    closeDetailsModal() {
      this.showDetailsModal = false;
      this.ticketDetails = null;
    },
    formatDate(dateString) {
      if (!dateString) return "—";
      try {
        return new Date(dateString).toLocaleDateString("pt-BR");
      } catch {
        return String(dateString);
      }
    },
    formatTime(dateString) {
      if (!dateString) return "";
      try {
        return new Date(dateString).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
      } catch {
        return "";
      }
    },
    formatDateTime(dateString) {
      if (!dateString) return "—";
      try {
        const d = new Date(dateString);
        return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
      } catch {
        return String(dateString);
      }
    },
    // Bingo Semanal (vários sorteios): "primeiro a último"; demais: data única
    formatTicketDrawDate(details) {
      if (!details) return "";
      const count = Number(details.packageDrawCount || details.PackageDrawCount || 1) || 1;
      const first =
        details.packageFirstDrawDateTime ||
        details.PackageFirstDrawDateTime ||
        details.betDateTime;
      const last =
        details.packageLastDrawDateTime ||
        details.PackageLastDrawDateTime;

      if (count > 1 && first && last) {
        return `${this.formatDateTime(first)} a ${this.formatDateTime(last)}`;
      }

      return this.formatDate(details.betDateTime);
    },
    setCurrencyString(value) {
      return jsFunctions.setCurrencyString(value);
    },
    isTicketClosed(ticket) {
      const isCanceled = ticket.canceled === true || ticket.canceled === "true";
      const totalAwarded = parseFloat(ticket.totalAwarded || 0);
      if (isCanceled || totalAwarded > 0) return false;
      const drawDate = ticket.dateTime || ticket.betDateTime;
      if (!drawDate) return false;
      return new Date() > new Date(drawDate);
    },
    isPrizeExpired(ticket) {
      const totalAwarded = parseFloat(ticket.totalAwarded || 0);
      const isPrizeClaimed = ticket.prizeClaimed === true || ticket.prizeClaimed === "true";
      if (totalAwarded <= 0 || isPrizeClaimed) return false;
      const betDateTime = ticket.dateTime || ticket.betDateTime;
      if (!betDateTime) return false;
      const drawDate = new Date(betDateTime);
      drawDate.setHours(0, 0, 0, 0);
      const expiryDate = new Date(drawDate);
      expiryDate.setDate(expiryDate.getDate() + (ticket.prizeExpirationDays || 7));
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return now > expiryDate;
    },
    getStatusClass(ticket) {
      const isCanceled = ticket.canceled === true || ticket.canceled === "true";
      const totalAwarded = parseFloat(ticket.totalAwarded || 0);
      const isClosed = this.isTicketClosed(ticket);
      const isPrizeClaimed = ticket.prizeClaimed === true || ticket.prizeClaimed === "true";
      const isExpired = this.isPrizeExpired(ticket);
      if (isCanceled) return "status-canceled";
      if (totalAwarded > 0 && isExpired && !isPrizeClaimed) return "status-expired";
      if (totalAwarded > 0) return "status-awarded";
      if (isClosed) return "status-closed";
      return "status-active";
    },
    getStatusIcon(ticket) {
      const isCanceled = ticket.canceled === true || ticket.canceled === "true";
      const totalAwarded = parseFloat(ticket.totalAwarded || 0);
      const isClosed = this.isTicketClosed(ticket);
      const isPrizeClaimed = ticket.prizeClaimed === true || ticket.prizeClaimed === "true";
      const isExpired = this.isPrizeExpired(ticket);
      if (isCanceled) return "fa-solid fa-times-circle";
      if (totalAwarded > 0 && isExpired && !isPrizeClaimed) return "fa-solid fa-clock";
      if (totalAwarded > 0) return "fa-solid fa-trophy";
      if (isClosed) return "fa-solid fa-lock";
      return "fa-solid fa-check-circle";
    },
    getStatusText(ticket) {
      const isCanceled = ticket.canceled === true || ticket.canceled === "true";
      const totalAwarded = parseFloat(ticket.totalAwarded || 0);
      const isClosed = this.isTicketClosed(ticket);
      const isPrizeClaimed = ticket.prizeClaimed === true || ticket.prizeClaimed === "true";
      const isExpired = this.isPrizeExpired(ticket);
      if (isCanceled) return "Cancelado";
      if (totalAwarded > 0 && isExpired && !isPrizeClaimed) return "Expirado";
      if (totalAwarded > 0 && isPrizeClaimed) return "Resgatado";
      if (totalAwarded > 0) return "Premiado";
      if (isClosed) return "Encerrado";
      return "Ativo";
    },
    getTotalAwardValue(awards) {
      if (!Array.isArray(awards)) return 0;
      return awards.reduce((sum, award) => sum + parseFloat(award.value || 0), 0);
    },
    getTotalPrizeValue(ticketDetails) {
      if (!ticketDetails?.lotteries?.length) return 0;
      let total = 0;
      ticketDetails.lotteries.forEach((lottery) => {
        (lottery.numbers || []).forEach((number) => {
          if (number.numberAwards?.length) total += this.getTotalAwardValue(number.numberAwards);
        });
      });
      return total;
    },
    isPrizeClaimedInDetails(ticketDetails) {
      return ticketDetails && (ticketDetails.prizeClaimed === true || ticketDetails.prizeClaimed === "true");
    },
    getStatusWithCancelDate(ticketDetails) {
      if (!ticketDetails) return "";
      if (ticketDetails.status === "Cancelado" && ticketDetails.canceledDateTime) {
        return "Cancelado " + this.formatDateTime(ticketDetails.canceledDateTime);
      }
      return ticketDetails.status || "";
    },
    groupNumbersByBetGroup(numbers) {
      if (!numbers?.length) return [];
      const groups = {};
      numbers.forEach((number) => {
        const groupOrder = number.betGroupOrder ?? 0;
        if (!groups[groupOrder]) groups[groupOrder] = [];
        groups[groupOrder].push(number);
      });
      return Object.keys(groups)
        .sort((a, b) => Number(a) - Number(b))
        .map((groupOrder) => ({ groupOrder: Number(groupOrder), numbers: groups[groupOrder] }));
    },
    getModalitiesInGroup(numbers) {
      if (!numbers?.length) return [];
      const modalityGroups = {};
      numbers.forEach((number) => {
        const key = `${number.modalityDescription}_${number.placing}`;
        if (!modalityGroups[key]) modalityGroups[key] = [];
        modalityGroups[key].push(number);
      });
      return Object.entries(modalityGroups)
        .sort((a, b) => {
          const plusA = a[1][0]?.plusValueType ?? 0;
          const plusB = b[1][0]?.plusValueType ?? 0;
          if (plusA !== plusB) return plusA - plusB;
          return (a[1][0]?.modalityDescription ?? "").localeCompare(b[1][0]?.modalityDescription ?? "");
        })
        .map((entry) => entry[1]);
    },
  },
};
</script>

<style scoped>
.prize-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9998;
  padding: 20px;
}
.prize-modal {
  background: var(--card-background, #fff);
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  max-width: 960px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--bg-secondary, #f8f9fa);
  border-bottom: 1px solid var(--border-color, #dee2e6);
}
.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  gap: 10px;
}
.btn-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: var(--text-secondary);
  font-size: 1.25rem;
}
.btn-close:hover {
  color: var(--text-primary);
}
.modal-subheader {
  padding: 10px 20px;
  background: var(--bg-secondary, #f8f9fa);
  border-bottom: 1px solid var(--border-color, #dee2e6);
  font-size: 0.9rem;
  color: var(--text-secondary);
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  justify-content: space-between;
  align-items: center;
}
.subheader-total {
  font-weight: 600;
  color: var(--text-primary, #212529);
}
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}
.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 12px;
  color: var(--text-secondary);
}
.error-state {
  color: var(--danger-color, #dc3545);
}
.table-container {
  overflow-x: auto;
}
.modern-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}
.modern-table th {
  background: var(--bg-secondary, #f8f9fa);
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid var(--border-color, #dee2e6);
}
.modern-table td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-color, #dee2e6);
}
.data-row:hover {
  background: rgba(0, 0, 0, 0.02);
}
.nsu-code {
  font-weight: 600;
  font-family: monospace;
}
.package-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: #0d6efd;
  color: #fff;
  vertical-align: middle;
}
.date { display: block; }
.time { font-size: 0.85rem; color: var(--text-secondary); }
.currency-value { font-weight: 600; }
.currency-positive { color: #28a745; }
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}
.status-canceled { background: #f8d7da; color: #721c24; }
.status-awarded { background: #fff3cd; color: #856404; }
.status-expired { background: #f0ad4e; color: #fff; }
.status-closed { background: #e2e3e5; color: #495057; }
.status-active { background: #d4edda; color: #155724; }
.btn-action {
  padding: 6px 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: var(--accent-color, #0d6efd);
  color: white;
}
.btn-action:hover { opacity: 0.9; }

/* Inner modal (detalhes) */
.inner-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}
.inner-modal-content {
  background: var(--card-background, #fff);
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
}
.ticket-details .detail-group {
  margin-bottom: 12px;
}
.ticket-details label {
  display: block;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 4px;
}
.lottery-draw-item {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}
.lotteries-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color, #dee2e6);
}
.lotteries-section h4 {
  margin-bottom: 12px;
  font-size: 1rem;
}
.lottery-item {
  margin-bottom: 16px;
}
.lottery-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.lottery-title,
.bet-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}
.bet-group {
  margin-bottom: 14px;
}
.modality-group {
  margin-bottom: 12px;
}
.modality-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 0.9rem;
}
.modality-name-container { display: flex; align-items: center; gap: 8px; }
.valendo-badge {
  font-size: 0.75rem;
  background: #ffc107;
  color: #000;
  padding: 2px 6px;
  border-radius: 4px;
}
.numbers-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.number-badge {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid var(--border-color, #dee2e6);
  border-radius: 8px;
  background: var(--bg-primary, #fff);
}
.number-badge.number-awarded {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border-color: #ffc107;
}
.number-value { font-weight: 700; font-size: 1.1rem; }
.number-amount { font-size: 0.85rem; color: var(--accent-color); }
.number-award-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 2px 6px;
  background: #28a745;
  color: white;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}
.badge { padding: 4px 10px; border-radius: 6px; font-size: 0.85rem; }
.badge-success { background: #d4edda; color: #155724; }
.badge-warning { background: #fff3cd; color: #856404; }
</style>
